import { every } from 'lodash';
import type { ValidationErrors } from '../../../../../utils/validation';
import { cardioDeviations } from '../../../../../../common/customControls/cardioDeviations';
import { InputCheckbox, InputText, ListHeader } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormGroup } from '../../../../elements/forms/FormGroup';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { InputControl } from '../../../../elements/forms/inputControl/InputControl';
import { List } from '../../../../elements/List/List';

export interface IAfwijkingen {
    afwijking_details: {
        av_geleiding: {
            anders: string | null;
            eerste_graads_av_blok: boolean;
            normale_geleiding: boolean;
            totaal_av_blok: boolean;
            tweede_graads_av_blok_type_mobitz: boolean;
            tweede_graads_av_blok_typewenckebach: boolean;
        };
        elektrische_hartas: {
            anders: string | null;
            extreme_as: boolean;
            horizontale_hartas: boolean;
            intermediair: boolean;
            linker_asdeviatie: boolean;
            rechter_asdeviatie: boolean;
            verticale_hartas: boolean;
        };
        overig: {
            anders: string | null;
            linker_boezem_overbelasting: boolean;
            prominente_u_golf: boolean;
            rechter_boezem_overbelasting: boolean;
        };
        qrs_geleiding: {
            anders: string | null;
            aspecifieke_iv_geleidingsvertraging: boolean;
            compleet_lbtb: boolean;
            compleet_rbtb: boolean;
            fasciculus_anticus_blok: boolean;
            fasciculus_posticus_blok: boolean;
            incompleet_lbtb: boolean;
            incompleet_rbtb: boolean;
            qrs_normale_breedte: boolean;
        };
        qrs_vorm: {
            achterwand_infarct: boolean;
            anders: string | null;
            hoog_lateraal_infarct: boolean;
            inferolateraal_infarct: boolean;
            laag_voltage_in_standaard_afleidingen: boolean;
            normaal_voltage: boolean;
            onderwand_infarct: boolean;
            pre_exitatie: boolean;
            tekenen_van_lvh: boolean;
            tekenen_van_rvh: boolean;
            trage_r_top_progressie_over_voorwand: boolean;
            voorwand_infarct: boolean;
        };
        ritme: {
            anders: string | null;
            atriaal_pacemakerritme: boolean;
            atriale_tachycardie: boolean;
            atriumfibrilleren: boolean;
            atriumflutter: boolean;
            av_nodaal_ritme: boolean;
            avnrt: boolean;
            ddd_pacemakerritme: boolean;
            ectopisch_boezemritme: boolean;
            fusie_complex: boolean;
            geblokkeerde_pac_bij_av_geleiding: boolean;
            niet_te_bepalen_ritme: boolean;
            prematuur_atriaal_complex: boolean;
            prematuur_ventriculair_complex: boolean;
            sa_block: boolean;
            sinus_arrest: boolean;
            sinusaritmie: boolean;
            sinusritme: boolean;
            sinustachycardie: boolean;
            supraventriculair_ritmestoornis: boolean;
            torsades_des_pointes: boolean;
            ventriculair_escaperitme: boolean;
            ventriculair_pacemakerritme: boolean;
            ventriculaire_tachycardie_non_sustained: boolean;
            ventriculaire_tachycardie_sustained: boolean;
        };
        st_segment: {
            aspecifieke_repolarisatiestoornissen: boolean;
            geen_ischemie: boolean;
            secundaire_repolarisatie_stoornissen: boolean;
            wel_ischemie: string | null;
        };
    };
    afwijking_in: {
        av_geleiding: boolean;
        elektrische_hartas: boolean;
        overig: boolean;
        qrs_geleiding: boolean;
        qrs_vorm: boolean;
        ritme: boolean;
        st_segment: boolean;
    };
}

interface IAddEditAfwijkingenProps {
    afwijkingen: IAfwijkingen;
    errors: ValidationErrors;
    onChange: (value: IAfwijkingen) => Promise<void>;
    onDoValidate: (name: 'afwijking_details' | 'afwijking_in', category?: string, disorder?: string) => Promise<void>;
}

export class AddEditAfwijkingen extends Component<IAddEditAfwijkingenProps, {}> {
    public render() {
        return (
            <HorizontalFormGroup>
                <FormLabel>Afwijking in</FormLabel>
                <InputContainer>
                    {this.afwijkingGroupCheckbox('Ritme', 'ritme')}
                    {this.props.afwijkingen.afwijking_in.ritme && (
                        <InputControl indented>{this.afwijkingCategoryCheckboxList('ritme')}</InputControl>
                    )}
                    {this.afwijkingGroupCheckbox('AV geleiding', 'av_geleiding')}
                    {this.props.afwijkingen.afwijking_in.av_geleiding && (
                        <InputControl indented>{this.afwijkingCategoryCheckboxList('av_geleiding')}</InputControl>
                    )}
                    {this.afwijkingGroupCheckbox('QRS geleiding', 'qrs_geleiding')}
                    {this.props.afwijkingen.afwijking_in.qrs_geleiding && (
                        <InputControl indented>{this.afwijkingCategoryCheckboxList('qrs_geleiding')}</InputControl>
                    )}
                    {this.afwijkingGroupCheckbox('QRS vorm', 'qrs_vorm')}
                    {this.props.afwijkingen.afwijking_in.qrs_vorm && (
                        <InputControl indented>{this.afwijkingCategoryCheckboxList('qrs_vorm')}</InputControl>
                    )}
                    {this.afwijkingGroupCheckbox('ST segment', 'st_segment')}
                    {this.props.afwijkingen.afwijking_in.st_segment && (
                        <InputControl indented>{this.afwijkingCategoryCheckboxList('st_segment')}</InputControl>
                    )}
                    {this.afwijkingGroupCheckbox('Elektrische hartas', 'elektrische_hartas')}
                    {this.props.afwijkingen.afwijking_in.elektrische_hartas && (
                        <InputControl indented>{this.afwijkingCategoryCheckboxList('elektrische_hartas')}</InputControl>
                    )}
                    {this.afwijkingGroupCheckbox('Overig', 'overig')}
                    {this.props.afwijkingen.afwijking_in.overig && (
                        <InputControl indented>{this.afwijkingCategoryCheckboxList('overig')}</InputControl>
                    )}
                </InputContainer>
            </HorizontalFormGroup>
        );
    }

    private afwijkingCategoryCheckboxList<Category extends keyof IAfwijkingen['afwijking_details']>(
        category: Category
    ) {
        return cardioDeviations[category].map((item) => {
            const key = item.key as string & keyof IAfwijkingen['afwijking_details'][Category];
            const value = this.props.afwijkingen.afwijking_details[category][key];
            if (typeof value === 'boolean') {
                return this.afwijkingCheckbox(item.name, category, key);
            } else {
                return this.afwijkingCheckboxAndInput(item.name, category, key);
            }
        });
    }

    private afwijkingGroupCheckbox(text: string, name: keyof IAfwijkingen['afwijking_in']) {
        return (
            <InputControl
                hasErrors={this.props.errors.has(`customControlData.afwijking_details.${name}`)}
                key={name}
            >
                <InputCheckbox
                    checked={this.props.afwijkingen.afwijking_in[name]}
                    error={this.props.errors.get(`customControlData.afwijking_details.${name}`)}
                    name={name}
                    onChange={async (value) => {
                        if (!value) {
                            const emptyAfwijkingen = getEmptyAfwijkingen();
                            await this.props.onChange({
                                ...this.props.afwijkingen,
                                afwijking_in: {
                                    ...this.props.afwijkingen.afwijking_in,
                                    [name]: value,
                                },
                                afwijking_details: {
                                    ...this.props.afwijkingen.afwijking_details,
                                    [name]: emptyAfwijkingen.afwijking_details[name],
                                },
                            });
                        } else {
                            await this.props.onChange({
                                ...this.props.afwijkingen,
                                afwijking_in: {
                                    ...this.props.afwijkingen.afwijking_in,
                                    [name]: value,
                                },
                            });
                        }
                    }}
                    onDoValidate={async () => {
                        if (this.props.errors.has(`customControlData.afwijking_details.${name}`)) {
                            await this.props.onDoValidate('afwijking_details', name);
                        }
                    }}
                    text={text}
                />
            </InputControl>
        );
    }

    private afwijkingCheckbox<Category extends keyof IAfwijkingen['afwijking_details']>(
        text: string,
        category: Category,
        name: string & keyof IAfwijkingen['afwijking_details'][Category]
    ) {
        const categoryData = this.props.afwijkingen.afwijking_details[category];

        return (
            <InputControl key={'afwijking_' + category + '_' + name}>
                <InputCheckbox
                    checked={Boolean(categoryData[name])}
                    name={'afwijking_' + category + '_' + name}
                    onChange={async (value) =>
                        await this.props.onChange({
                            ...this.props.afwijkingen,
                            afwijking_details: {
                                ...this.props.afwijkingen.afwijking_details,
                                [category]: { ...(categoryData as {}), [name]: value },
                            },
                        })
                    }
                    onDoValidate={async () => {
                        if (this.props.errors.has(`customControlData.afwijking_details.${category}`)) {
                            await this.props.onDoValidate('afwijking_details', category);
                        }
                    }}
                    text={text}
                />
            </InputControl>
        );
    }

    private afwijkingCheckboxAndInput<Category extends keyof IAfwijkingen['afwijking_details']>(
        text: string,
        category: Category,
        name: string & keyof IAfwijkingen['afwijking_details'][Category]
    ) {
        const categoryData = this.props.afwijkingen.afwijking_details[category];

        return (
            <FormGroup
                hasErrors={this.props.errors.has(`customControlData.afwijking_details.${category}.${name}`)}
                isGrouped
                key={category + name}
            >
                <InputCheckbox
                    checked={categoryData[name] !== null}
                    key={'afwijking_' + category + '_' + name}
                    name={'afwijking_' + category + '_' + name}
                    onChange={async (value) =>
                        await this.props.onChange({
                            ...this.props.afwijkingen,
                            afwijking_details: {
                                ...this.props.afwijkingen.afwijking_details,
                                [category]: { ...(categoryData as {}), [name]: value ? '' : null },
                            },
                        })
                    }
                    onDoValidate={async () => {
                        if (this.props.errors.has(`customControlData.afwijking_details.${category}`)) {
                            await this.props.onDoValidate('afwijking_details', category);
                        }
                        if (this.props.errors.has(`customControlData.afwijking_details.${category}.${name}`)) {
                            await this.props.onDoValidate('afwijking_details', category, name.toString());
                        }
                    }}
                    text=""
                />

                <InputText
                    className="has-checkbox"
                    error={
                        this.props.errors &&
                        this.props.errors.get(`customControlData.afwijking_details.${category}.${name}`)
                    }
                    onChange={async (value) =>
                        await this.props.onChange({
                            ...this.props.afwijkingen,
                            afwijking_details: {
                                ...this.props.afwijkingen.afwijking_details,
                                [category]: { ...(categoryData as {}), [name]: value === '' ? null : value },
                            },
                        })
                    }
                    onDoValidate={async () =>
                        await this.props.onDoValidate('afwijking_details', category, name.toString())
                    }
                    placeholder={text}
                    value={String(categoryData[name] || '')}
                />
            </FormGroup>
        );
    }
}

export function ReadAfwijkingen(props: { afwijkingen: IAfwijkingen }) {
    return (
        <HorizontalFormGroupRead>
            <FormLabel>Afwijking in</FormLabel>
            <InputContainer>
                {props.afwijkingen.afwijking_in.ritme && (
                    <>
                        <ListHeader>Ritme:</ListHeader>{' '}
                        <List items={displayAfwijkingCategory('ritme', props.afwijkingen)} />
                    </>
                )}
                {props.afwijkingen.afwijking_in.av_geleiding && (
                    <>
                        <ListHeader>AV geleiding:</ListHeader>{' '}
                        <List items={displayAfwijkingCategory('av_geleiding', props.afwijkingen)} />
                    </>
                )}
                {props.afwijkingen.afwijking_in.qrs_geleiding && (
                    <>
                        <ListHeader>QRS geleiding:</ListHeader>{' '}
                        <List items={displayAfwijkingCategory('qrs_geleiding', props.afwijkingen)} />
                    </>
                )}
                {props.afwijkingen.afwijking_in.qrs_vorm && (
                    <>
                        <ListHeader>QRS vorm:</ListHeader>{' '}
                        <List items={displayAfwijkingCategory('qrs_vorm', props.afwijkingen)} />
                    </>
                )}
                {props.afwijkingen.afwijking_in.st_segment && (
                    <>
                        <ListHeader>ST segment:</ListHeader>{' '}
                        <List items={displayAfwijkingCategory('st_segment', props.afwijkingen)} />
                    </>
                )}
                {props.afwijkingen.afwijking_in.elektrische_hartas && (
                    <>
                        <ListHeader>Elektrische hartas:</ListHeader>{' '}
                        <List items={displayAfwijkingCategory('elektrische_hartas', props.afwijkingen)} />
                    </>
                )}
                {props.afwijkingen.afwijking_in.overig && (
                    <>
                        <ListHeader>Overig:</ListHeader>{' '}
                        <List items={displayAfwijkingCategory('overig', props.afwijkingen)} />
                    </>
                )}
            </InputContainer>
        </HorizontalFormGroupRead>
    );
}

export function showAfwijkingen(txcsProcesId: string | null, customControlData: any, conclusieFieldName?: string) {
    if (customControlData) {
        const conclusieKey = conclusieFieldName ? conclusieFieldName : 'conclusie';
        const conclusie: string | null = customControlData[conclusieKey] ? customControlData[conclusieKey] : null;
        const afwijkingIn: IAfwijkingen['afwijking_in'] | null = customControlData.afwijking_in
            ? customControlData.afwijking_in
            : null;

        if (afwijkingIn && ((conclusie && conclusie === 'Afwijkend') || txcsProcesId !== null)) {
            if (
                afwijkingIn.ritme ||
                afwijkingIn.st_segment ||
                afwijkingIn.qrs_vorm ||
                afwijkingIn.qrs_geleiding ||
                afwijkingIn.overig ||
                afwijkingIn.elektrische_hartas ||
                afwijkingIn.av_geleiding
            ) {
                return true;
            }
        }
    }

    return false;
}

function displayAfwijkingCategory<Category extends keyof IAfwijkingen['afwijking_details']>(
    category: Category,
    afwijkingen: IAfwijkingen
) {
    return cardioDeviations[category]
        .map((item) => {
            const key = item.key as keyof IAfwijkingen['afwijking_details'][Category];
            const value = afwijkingen.afwijking_details[category][key];
            if (typeof value === 'boolean' && value) {
                return item.name;
            } else if (typeof value === 'string' && value !== null) {
                return value;
            } else {
                return null;
            }
        })
        .filter((item) => item !== null) as string[];
}

export function getEmptyAfwijkingen(): IAfwijkingen {
    return {
        afwijking_in: {
            ritme: false,
            av_geleiding: false,
            qrs_geleiding: false,
            qrs_vorm: false,
            st_segment: false,
            elektrische_hartas: false,
            overig: false,
        },
        afwijking_details: {
            ritme: {
                sinusritme: false,
                atriumfibrilleren: false,
                atriumflutter: false,
                sinusaritmie: false,
                sa_block: false,
                sinus_arrest: false,
                ectopisch_boezemritme: false,
                atriale_tachycardie: false,
                sinustachycardie: false,
                ventriculaire_tachycardie_sustained: false,
                ventriculaire_tachycardie_non_sustained: false,
                torsades_des_pointes: false,
                supraventriculair_ritmestoornis: false,
                av_nodaal_ritme: false,
                avnrt: false,
                geblokkeerde_pac_bij_av_geleiding: false,
                prematuur_atriaal_complex: false,
                prematuur_ventriculair_complex: false,
                fusie_complex: false,
                ventriculair_pacemakerritme: false,
                ddd_pacemakerritme: false,
                atriaal_pacemakerritme: false,
                ventriculair_escaperitme: false,
                niet_te_bepalen_ritme: false,
                anders: null,
            },
            av_geleiding: {
                normale_geleiding: false,
                eerste_graads_av_blok: false,
                tweede_graads_av_blok_type_mobitz: false,
                tweede_graads_av_blok_typewenckebach: false,
                totaal_av_blok: false,
                anders: null,
            },
            qrs_geleiding: {
                qrs_normale_breedte: false,
                fasciculus_anticus_blok: false,
                fasciculus_posticus_blok: false,
                compleet_rbtb: false,
                incompleet_rbtb: false,
                compleet_lbtb: false,
                incompleet_lbtb: false,
                aspecifieke_iv_geleidingsvertraging: false,
                anders: null,
            },
            qrs_vorm: {
                normaal_voltage: false,
                laag_voltage_in_standaard_afleidingen: false,
                tekenen_van_lvh: false,
                tekenen_van_rvh: false,
                pre_exitatie: false,
                trage_r_top_progressie_over_voorwand: false,
                voorwand_infarct: false,
                onderwand_infarct: false,
                hoog_lateraal_infarct: false,
                inferolateraal_infarct: false,
                achterwand_infarct: false,
                anders: null,
            },
            st_segment: {
                geen_ischemie: false,
                secundaire_repolarisatie_stoornissen: false,
                aspecifieke_repolarisatiestoornissen: false,
                wel_ischemie: null,
            },
            elektrische_hartas: {
                intermediair: false,
                linker_asdeviatie: false,
                rechter_asdeviatie: false,
                extreme_as: false,
                horizontale_hartas: false,
                verticale_hartas: false,
                anders: null,
            },
            overig: {
                linker_boezem_overbelasting: false,
                rechter_boezem_overbelasting: false,
                prominente_u_golf: false,
                anders: null,
            },
        },
    };
}

export function checkForEmptySubcategoriesCardioAfwijkingen(
    afwijkingenIn: { [k: string]: boolean },
    afwijkingenDetails: { [k: string]: { [k: string]: boolean | string | null } },
    errors: ValidationErrors
) {
    for (const category of Object.keys(afwijkingenIn)) {
        if (
            afwijkingenIn[category] &&
            every(afwijkingenDetails[category], (value) => value === false || value === null)
        ) {
            errors = errors.set('customControlData.afwijking_details.' + category, 'selectAtLeastOneSubCategory');
        }
    }

    return errors;
}
